/**
 * Builds a procedural road/path ribbon from a polyline (see types.ts's `Road`).
 *
 * The author places points; this generates the geometry between them — the
 * city-builder model, rather than placing individual road-segment props.
 * Shared between the live game and `ultra_map_editor`, like `decal-mesh.ts`,
 * so the editor preview cannot drift from what ships.
 *
 * THE POINT OF THIS FILE IS THE UV MAPPING. A road is textured with `u`
 * ACROSS the ribbon and `v` along its ARC LENGTH, in metres-per-tile — not
 * per-segment 0..1. That distinction is what makes a masonry path read as
 * laid pavers: the paving pattern advances at a constant rate along the path
 * and is identical through a straight and a corner. Mapping v per-segment
 * instead would stretch the pattern in proportion to segment length, so a
 * long straight and a short corner piece would show visibly different paver
 * sizes, and the texture would visibly pop at every point.
 *
 * Corners are MITRED: an interior point offsets along the bisector of its two
 * segments, lengthened by 1/cos(half-angle) so the ribbon keeps its authored
 * width through the turn instead of pinching. The miter is capped, because
 * that factor diverges as a corner approaches a full doubling-back and would
 * otherwise fire a spike across the map.
 *
 * Y placement: 0.005, deliberately BELOW `Decal`'s 0.01. A puddle or a
 * newspaper should lie ON a road, not be hidden inside it. `polygonOffset`
 * carries the rest of the load, since 5 mm is not much depth separation at
 * this camera's draw distance.
 */
import * as THREE from 'three';
import type { Road, RoadSurface, Vec2 } from '../types';

/**
 * Per-surface texture + default tiling. `tileLength` is metres of road per
 * texture repeat ALONG the road; it differs per surface because the features
 * differ in real-world size — paving stones repeat every couple of metres,
 * a cracked asphalt sheet over a much longer run before the eye spots it.
 */
export const ROAD_SURFACES: Readonly<Record<RoadSurface, { textureUrl: string; tileLength: number }>> = {
  dirt_road: { textureUrl: '/textures/roads/road-dirt.webp', tileLength: 6 },
  dirt_path: { textureUrl: '/textures/roads/path-dirt.webp', tileLength: 4 },
  asphalt_old: { textureUrl: '/textures/roads/road-asphalt.webp', tileLength: 8 },
  masonry_path: { textureUrl: '/textures/roads/path-masonry.webp', tileLength: 2.5 },
};

/** Height above the ground plane. Below Decal's 0.01 — see the doc comment. */
export const ROAD_Y = 0.005;

/**
 * Per-layer Y step, so two overlapping roads (e.g. a dirt path crossing a
 * paved street) don't sit exactly coplanar. `polygonOffset` biases a mesh's
 * depth against *other* geometry, not against another instance of itself —
 * two roads both at the same `ROAD_Y` with the same offset still z-fight.
 * `buildRoads` applies `index * ROAD_LAYER_STEP` in author order. Kept small
 * enough that even the longest realistic road stack (author order, one road
 * per checklist item — currently 7) stays below Decal's 0.01 ceiling.
 */
const ROAD_LAYER_STEP = 0.0004;

/**
 * Cap on the miter lengthening factor. At a 60° turn the exact factor is 2;
 * beyond that the corner is sharp enough that a clean miter is impossible and
 * clamping (slightly pinching the outer edge) looks far better than the spike
 * an uncapped 1/cos would produce as the angle approaches 180°.
 */
const MAX_MITER = 2.0;

const textureLoader = new THREE.TextureLoader();

/** Drops consecutive duplicate points — a click-to-place UI emits them easily. */
function dedupe(points: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && Math.hypot(p.x - last.x, p.z - last.z) < 1e-6) continue;
    out.push({ x: p.x, z: p.z });
  }
  return out;
}

export interface RoadGeometryData {
  positions: Float32Array;
  uvs: Float32Array;
  normals: Float32Array;
  indices: number[];
  /** Total centreline length, metres — useful to tests and to the editor's readout. */
  length: number;
}

/**
 * The pure geometry half, with no Three.js objects — so it can be unit-tested
 * in Node against exact numbers (ribbon width, arc-length UVs, miter
 * behaviour) without a renderer or a GL context.
 */
export function roadGeometryData(road: Road): RoadGeometryData {
  const pts = dedupe(road.points);
  if (pts.length < 2) {
    throw new Error(`road "${road.id}" needs at least 2 distinct points, got ${pts.length}`);
  }
  const half = road.width / 2;
  const tile = road.tileLength ?? ROAD_SURFACES[road.surface].tileLength;

  // Per-segment unit directions in XZ.
  const dirs: Array<{ x: number; z: number }> = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const dx = pts[i + 1].x - pts[i].x;
    const dz = pts[i + 1].z - pts[i].z;
    const len = Math.hypot(dx, dz);
    dirs.push({ x: dx / len, z: dz / len });
  }

  // Cumulative arc length at each point — this is what v is derived from.
  const arc: number[] = [0];
  for (let i = 0; i < pts.length - 1; i++) {
    arc.push(arc[i] + Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].z - pts[i].z));
  }

  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];

  for (let i = 0; i < pts.length; i++) {
    const dPrev = dirs[i - 1];
    const dNext = dirs[i];
    // Tangent: the segment direction at an end, the averaged direction inside.
    let tx: number;
    let tz: number;
    if (!dPrev) { tx = dNext.x; tz = dNext.z; }
    else if (!dNext) { tx = dPrev.x; tz = dPrev.z; }
    else {
      tx = dPrev.x + dNext.x;
      tz = dPrev.z + dNext.z;
      const tl = Math.hypot(tx, tz);
      // A full doubling-back gives a zero-length bisector; fall back to the
      // incoming direction rather than emitting NaN across the buffer.
      if (tl < 1e-6) { tx = dPrev.x; tz = dPrev.z; } else { tx /= tl; tz /= tl; }
    }
    // Left normal in XZ (rotate the tangent +90° about Y).
    let nx = -tz;
    let nz = tx;

    // Miter: widen the offset so the ribbon keeps `width` through the corner.
    let miter = 1;
    if (dPrev && dNext) {
      // cos of the half-angle between the two segments = dot(bisector, dir).
      const cosHalf = nx * -dPrev.z + nz * dPrev.x;
      if (Math.abs(cosHalf) > 1e-6) miter = Math.min(1 / Math.abs(cosHalf), MAX_MITER);
    }
    const ox = nx * half * miter;
    const oz = nz * half * miter;
    const v = arc[i] / tile;

    // left edge (u = 0) then right edge (u = 1)
    positions.push(pts[i].x + ox, 0, pts[i].z + oz);
    uvs.push(0, v);
    normals.push(0, 1, 0);
    positions.push(pts[i].x - ox, 0, pts[i].z - oz);
    uvs.push(1, v);
    normals.push(0, 1, 0);
  }

  const indices: number[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = i * 2;
    const b = a + 1;
    const c = a + 2;
    const d = a + 3;
    // Wound so the face normal is +Y (visible from above).
    indices.push(a, c, b, b, c, d);
  }

  return {
    positions: new Float32Array(positions),
    uvs: new Float32Array(uvs),
    normals: new Float32Array(normals),
    indices,
    length: arc[arc.length - 1],
  };
}

export function buildRoadMesh(road: Road, layerIndex = 0): THREE.Object3D {
  const data = roadGeometryData(road);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(data.uvs, 2));
  geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  geo.setIndex(data.indices);

  const texture = textureLoader.load(ROAD_SURFACES[road.surface].textureUrl);
  texture.colorSpace = THREE.SRGBColorSpace;
  // v runs well past 1 (arc length / tileLength), so wrapping is not optional
  // — clamped, the whole road would show one stretched tile.
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // Same reasoning as the ground plane: this is viewed at a grazing angle.
  texture.anisotropy = 8;

  const opacity = road.opacity ?? 1;
  const material = new THREE.MeshStandardMaterial({
    map: texture,
    roughness: 0.95,
    metalness: 0,
    transparent: opacity < 1,
    opacity,
    // Lit and ground-lying, so it should take the settlement's shadows.
    side: THREE.FrontSide,
    // 5 mm of separation is thin at this draw distance; nudge in depth too.
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    depthWrite: opacity >= 1,
  });

  const mesh = new THREE.Mesh(geo, material);
  mesh.receiveShadow = true;
  mesh.position.y = ROAD_Y + layerIndex * ROAD_LAYER_STEP;
  // Vertices are already in world XZ, so the mesh itself sits at the origin —
  // unlike a Decal, a road has no single position to offset from.
  return mesh;
}

/** All roads in one group, in author order — each gets a distinct Y layer so overlapping roads don't z-fight. */
export function buildRoads(roads: readonly Road[] | undefined): THREE.Group {
  const group = new THREE.Group();
  group.name = 'roads';
  (roads ?? []).forEach((road, index) => {
    const mesh = buildRoadMesh(road, index);
    mesh.userData.roadId = road.id;
    group.add(mesh);
  });
  return group;
}
